import { createContext, useContext, type ReactNode } from "react";
import {
  useAgencySiteByHandle,
  useAgencySiteByDomain,
  type AgencySiteData,
} from "@/hooks/queries/site/useAgencySite";
import { resolveTenantHost, type TenantHostInfo } from "@/lib/tenant/host";

interface TenantContextValue {
  host: TenantHostInfo;
  data: AgencySiteData | null;
  isLoading: boolean;
  /** True when the host expects a tenant but none could be resolved. */
  isUnknownTenant: boolean;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

/**
 * Resolves the active tenant from `window.location.hostname` and exposes
 * the matching agency_websites + agencies rows (or null) via context.
 *
 * Resolution order:
 *  1. Subdomain on a TENANT_ROOT_DOMAIN  → fetch by agency.public_handle
 *  2. Other (custom) domain              → fetch by agency_websites.custom_domain
 *  3. Platform / tenant-root             → no tenant; render children directly
 */
export function TenantProvider({ children }: { children: ReactNode }) {
  const host = resolveTenantHost();

  const handleQuery = useAgencySiteByHandle(
    host.mode === "subdomain" ? host.handle : undefined,
  );
  const domainQuery = useAgencySiteByDomain(
    host.mode === "custom-domain" ? host.customDomain : undefined,
  );

  const active = host.mode === "subdomain" ? handleQuery : domainQuery;
  const data = (active.data as AgencySiteData | null | undefined) ?? null;
  const isLoading =
    (host.mode === "subdomain" || host.mode === "custom-domain") && active.isLoading;

  const isUnknownTenant =
    !isLoading &&
    (host.mode === "subdomain" || host.mode === "custom-domain") &&
    !data;

  return (
    <TenantContext.Provider value={{ host, data, isLoading, isUnknownTenant }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error("useTenant must be used inside <TenantProvider>");
  }
  return ctx;
}

/**
 * Generic branded fallback for unknown tenants (e.g. bare focusagency.site,
 * or a subdomain with no matching agency). Keeps SEO sane and avoids the
 * redirect loop pattern that previously sent everything to focuscasting.com.
 */
export function TenantFallback({ host }: { host: TenantHostInfo }) {
  return (
    <div className="min-h-dvh flex flex-col items-center justify-center text-center px-6 bg-background text-foreground">
      <div className="max-w-md">
        <h1 className="text-3xl font-semibold mb-4">No agency site here yet</h1>
        <p className="text-muted-foreground mb-2">
          {host.mode === "subdomain"
            ? `We couldn't find an agency at "${host.hostname}".`
            : host.mode === "custom-domain"
              ? `No site is connected to ${host.hostname} yet.`
              : "This address isn't connected to an agency site."}
        </p>
        <p className="text-muted-foreground text-sm">
          If you reached this page by mistake, check the address and try again.
        </p>
      </div>
    </div>
  );
}
